
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProfileFormValues } from "@/components/profile/ProfileForm";
import { useAuth } from "@/contexts/AuthContext";

export const useProfileData = (profileId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string>("user");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  
  const userId = profileId || user?.id;

  useEffect(() => {
    async function fetchProfile() {
      if (!userId) return;

      try {
        setLoading(true);
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;

        // Get the user's role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .single();

        if (profileData) {
          setProfile(profileData);
          setAvatarUrl(profileData.avatar_url);
        }

        if (roleData && !roleError) {
          setRole(roleData.role);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast({
          title: "Failed to load profile",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [userId, user?.id, toast]);

  const updateProfile = async (values: ProfileFormValues) => {
    if (!userId) return;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: values.full_name || null,
          title: values.title || null,
          bio: values.bio || null,
          location: values.location || null,
          website: values.website || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: "Profile updated successfully",
      });
      
      // Refetch profile to get the latest data
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (fetchError) throw fetchError;
      
      setProfile(data);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Failed to update profile",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpdated = (url: string | null) => {
    setAvatarUrl(url);
  };

  const getFormValues = (): ProfileFormValues => ({
    full_name: profile?.full_name || "",
    title: profile?.title || "",
    bio: profile?.bio || "",
    location: profile?.location || "",
    website: profile?.website || ""
  });

  return {
    profile,
    loading,
    role,
    avatarUrl,
    updateProfile,
    handleAvatarUpdated,
    getFormValues
  };
};
